import { useContext, useMemo, useState } from "react";
import { sharedLib } from "@shared/services/shared.service";
import {
  ApiGeneralDataResponse,
  BotType,
  DisplayCurrency,
  MainCoinsTotalAndAvailableAmountData,
  QueryStatus,
} from "@shared/types";
import { skipToken } from "@reduxjs/toolkit/query";
import { toFixed } from "@shared/utils/numbers";
import { DISPLAY_CURRENCIES } from "@shared/common/constants";
import { CONNECT_TELEGRAM_INFO } from "@shared/features/onboarding/constants";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { isSymbol } from "@shared/types/utils";
import { useBotFormAccountAmounts } from "@shared/features/bots/BotForm/common/hooks/useBotFormAccountAmounts";
import { useClientsData } from "@shared/utils/hooks/misc";

export const useAccountAmount = (skip?: boolean) => {
  const { botType, account } = useContext(BotFormContext) || {};
  const { getMainCoinsAvailableAmountDataStatus } = useBotFormAccountAmounts({
    botType: botType ?? BotType.SIGNAL,
    account: isSymbol(account) ? undefined : account,
    skip,
  });

  const accountTotalAndAvailableAmountQuery =
    getMainCoinsAvailableAmountDataStatus as QueryStatus<
      ApiGeneralDataResponse<MainCoinsTotalAndAvailableAmountData>
    >;

  const getAccountAmountByCurrency = (
    accountId: number | undefined,
    currency: DisplayCurrency,
    amountType: "available" | "total"
  ) => {
    if (!accountId) return 0;
    const precision = currency === DISPLAY_CURRENCIES.USD ? 2 : 5;
    const amount =
      accountTotalAndAvailableAmountQuery.data?.data?.client?.[accountId]?.[
        amountType
      ]?.[currency];
    if (amount) return toFixed(amount, precision);
    return 0;
  };

  const getAccountMainCoinUsdAmount = (
    accountId: number,
    amountType: "available" | "total",
    coin: string
  ) => {
    return toFixed(
      accountTotalAndAvailableAmountQuery.data?.data?.main_coin?.[amountType]?.[
        accountId
      ]?.[coin]?.usd_amount,
      2
    );
  };

  const getAccountMainCoinData = (accountId: number, coin: string) => {
    return {
      total: getAccountMainCoinUsdAmount(accountId, "total", coin),
      available: getAccountMainCoinUsdAmount(accountId, "available", coin),
    };
  };

  return {
    getAccountAmountByCurrency,
    accountTotalAndAvailableAmountQuery: getMainCoinsAvailableAmountDataStatus,
    getAccountMainCoinData,
  };
};

export const useCopyDcaBot = () => {
  const { clientExchanges } = useClientsData();
  const baseQuery = useMemo(() => {
    return {
      by_tag: true,
      strict_symbol: true,
    };
  }, []);
  const getPublicBotsQuery =
    sharedLib.sharedApi.bots.useGetPublicBotsByTagQuery(baseQuery);

  const publicBotsData = getPublicBotsQuery.data?.data;
  const getAllPublicBots =
    getPublicBotsQuery.isSuccess && !publicBotsData?.high_volume_score.length;

  const getAllPublicBotsQuery =
    sharedLib.sharedApi.bots.useGetPublicBotsByTagQuery(
      getAllPublicBots
        ? {
            ...baseQuery,
            valid_bot_amount_only: false,
            exchanges: clientExchanges,
          }
        : skipToken
    );

  const allPublicBotsData = getAllPublicBotsQuery.data?.data;
  const publicBots = allPublicBotsData || publicBotsData;

  const isLoading =
    getPublicBotsQuery.isFetching || getAllPublicBotsQuery.isFetching;

  return { publicBots, isLoading };
};

export const useTelegramUserPreview = (
  removeUuid?: () => void,
  showRemoveUuidButton?: boolean,
  showingContinueWithoutTelegram?: boolean
) => {
  const [
    removeUuidConfirmationModalVisible,
    setRemoveUuidConfirmationModalVisible,
  ] = useState(false);

  const removeUuidModalData = {
    title: "Remove Telegram Account",
    isVisible: removeUuidConfirmationModalVisible,
    onCancel: () => setRemoveUuidConfirmationModalVisible(false),
    description: [
      CONNECT_TELEGRAM_INFO,
      "Are you sure you wish you remove this Telegram account?",
    ],
    onOk: () => {
      removeUuid?.();
      setRemoveUuidConfirmationModalVisible(false);
    },
  };

  let tooltipNotYourAccountText = "";
  if (showRemoveUuidButton) {
    tooltipNotYourAccountText =
      "Not your account? Click the X button on the right.";
  } else if (showingContinueWithoutTelegram) {
    tooltipNotYourAccountText =
      "Not your account? Click the 'Continue without connecting Telegram' button on the bottom.";
  }

  return {
    removeUuidModalData,
    openRemoveUuidConfirmationModal: () =>
      setRemoveUuidConfirmationModalVisible(true),
    tooltipNotYourAccountText,
  };
};
